import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Chip,
  Card,
  CardContent,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Avatar,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Assessment as ReportIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon,
  Store as StoreIcon,
  Inventory as StockIcon,
  ShoppingCart as SalesIcon,
  LocalShipping as TransferIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
} from "@mui/icons-material";

const HQDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState("thisMonth");

  // Sample HQ data
  const hqData = {
    totalStores: 5,
    activeStores: 4,
    totalRevenue: 15847352,
    monthlyGrowth: 12.5,
    totalStock: 87500,
    lowStockItems: 23,
    totalSales: 4523,
    pendingTransfers: 8,
  };

  // Store Performance Data
  const storePerformance = [
    {
      id: 1,
      code: "HQ001",
      name: "HQ - Mumbai",
      type: "Headquarters",
      revenue: 5500000,
      sales: 0,
      stock: 15000,
      growth: 8.5,
      status: "Active",
    },
    {
      id: 2,
      code: "WH001",
      name: "Warehouse - Navi Mumbai",
      type: "Warehouse",
      revenue: 0,
      sales: 0,
      stock: 50000,
      growth: 0,
      status: "Active",
    },
    {
      id: 3,
      code: "RT001",
      name: "Retail - Andheri",
      type: "Retail",
      revenue: 4250000,
      sales: 1850,
      stock: 8500,
      growth: 15.2,
      status: "Active",
    },
    {
      id: 4,
      code: "RT002",
      name: "Retail - Pune",
      type: "Retail",
      revenue: 3845000,
      sales: 1620,
      stock: 7200,
      growth: 11.8,
      status: "Active",
    },
    {
      id: 5,
      code: "RT003",
      name: "Retail - Thane",
      type: "Retail",
      revenue: 2252352,
      sales: 1053,
      stock: 6800,
      growth: -3.2,
      status: "Inactive",
    },
  ];

  // Top Products
  const topProducts = [
    { product: "Samsung Galaxy S23", category: "Electronics", sales: 485, revenue: 38803150 },
    { product: "Nike Running Shoes", category: "Footwear", sales: 892, revenue: 4457080 },
    { product: "Dell Laptop i5", category: "Electronics", sales: 358, revenue: 19687542 },
    { product: "Adidas T-Shirt", category: "Apparel", sales: 1250, revenue: 3124750 },
    { product: "iPhone 15 Pro", category: "Electronics", sales: 312, revenue: 37439688 },
  ];

  // Recent Transfers
  const recentTransfers = [
    {
      id: 1,
      transferNo: "TR001",
      from: "WH001 - Warehouse",
      to: "RT001 - Andheri",
      items: 15,
      status: "Completed",
      date: "2024-01-20",
    },
    {
      id: 2,
      transferNo: "TR002",
      from: "WH001 - Warehouse",
      to: "RT002 - Pune",
      items: 12,
      status: "In Transit",
      date: "2024-01-21",
    },
    {
      id: 3,
      transferNo: "TR003",
      from: "RT001 - Andheri",
      to: "RT003 - Thane",
      items: 5,
      status: "Pending",
      date: "2024-01-21",
    },
  ];

  // Category Performance
  const categoryPerformance = [
    { category: "Electronics", revenue: 95930380, percentage: 60.5, growth: 18.3 },
    { category: "Footwear", revenue: 31562400, percentage: 19.9, growth: 12.1 },
    { category: "Apparel", revenue: 31071872, percentage: 19.6, growth: 8.7 },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getGrowthColor = (growth) => {
    return growth >= 0 ? 'success.main' : 'error.main';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return { bg: '#e8f5e9', color: '#2e7d32' };
      case 'Inactive': return { bg: '#ffebee', color: '#d32f2f' };
      case 'Completed': return { bg: '#e8f5e9', color: '#2e7d32' };
      case 'In Transit': return { bg: '#e3f2fd', color: '#1976d2' };
      case 'Pending': return { bg: '#fff3e0', color: '#e65100' };
      default: return { bg: '#f5f5f5', color: '#666' };
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ReportIcon sx={{ color: 'primary.main', fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary">
              Headquarters Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Multi-store analytics and performance reports
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              label="Period"
            >
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="thisWeek">This Week</MenuItem>
              <MenuItem value="thisMonth">This Month</MenuItem>
              <MenuItem value="thisQuarter">This Quarter</MenuItem>
              <MenuItem value="thisYear">This Year</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            sx={{ borderRadius: 2 }}
          >
            Print
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{
              borderRadius: 2,
              backgroundColor: 'primary.main',
              '&:hover': { backgroundColor: 'primary.dark' },
            }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Main Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #1976d2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Revenue</Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary.main">
                    {formatCurrency(hqData.totalRevenue)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                    <Typography variant="caption" color="success.main" fontWeight="600">
                      +{hqData.monthlyGrowth}%
                    </Typography>
                  </Box>
                </Box>
                <MoneyIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #2e7d32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Active Stores</Typography>
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    {hqData.activeStores} / {hqData.totalStores}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {((hqData.activeStores / hqData.totalStores) * 100).toFixed(0)}% operational
                  </Typography>
                </Box>
                <StoreIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #ff9800' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Sales</Typography>
                  <Typography variant="h5" fontWeight="bold" color="warning.main">
                    {hqData.totalSales.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">orders</Typography>
                </Box>
                <SalesIcon sx={{ fontSize: 48, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #9c27b0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Stock</Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: '#9c27b0' }}>
                    {hqData.totalStock.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="error.main">
                    {hqData.lowStockItems} low stock items
                  </Typography>
                </Box>
                <StockIcon sx={{ fontSize: 48, color: '#9c27b0', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Store Performance" icon={<StoreIcon />} iconPosition="start" />
          <Tab label="Category Analysis" icon={<ReportIcon />} iconPosition="start" />
          <Tab label="Top Products" icon={<SalesIcon />} iconPosition="start" />
          <Tab label="Inter-Store Transfers" icon={<TransferIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: Store Performance */}
      {activeTab === 0 && (
        <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6" fontWeight="bold">Store Performance Overview</Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Store Code</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Store Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Revenue</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Sales</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Stock</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Growth</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {storePerformance.map((store) => (
                  <TableRow key={store.id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="700" sx={{ fontFamily: 'monospace' }}>
                        {store.code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: '0.875rem' }}>
                          {store.code.charAt(0)}
                        </Avatar>
                        <Typography variant="body2" fontWeight="600">{store.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={store.type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="700" color="primary">
                        {store.revenue > 0 ? formatCurrency(store.revenue) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {store.sales > 0 ? store.sales.toLocaleString() : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {store.stock.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">units</Typography>
                    </TableCell>
                    <TableCell>
                      {store.growth !== 0 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {store.growth >= 0 ? (
                            <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                          ) : (
                            <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />
                          )}
                          <Typography
                            variant="body2"
                            fontWeight="600"
                            sx={{ color: getGrowthColor(store.growth) }}
                          >
                            {store.growth > 0 ? '+' : ''}{store.growth}%
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={store.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(store.status).bg,
                          color: getStatusColor(store.status).color,
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tab 1: Category Analysis */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          {categoryPerformance.map((cat, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {cat.category}
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
                    {formatCurrency(cat.revenue)}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Market Share
                      </Typography>
                      <Typography variant="caption" fontWeight="600">
                        {cat.percentage}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={cat.percentage}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: index === 0 ? '#1976d2' : index === 1 ? '#ff9800' : '#2e7d32',
                        },
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                    <Typography variant="body2" color="success.main" fontWeight="600">
                      +{cat.growth}% growth
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Category Distribution
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Total Revenue: {formatCurrency(categoryPerformance.reduce((sum, cat) => sum + cat.revenue, 0))}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Tab 2: Top Products */}
      {activeTab === 2 && (
        <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6" fontWeight="bold">Top Performing Products</Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Rank</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Sales</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Revenue</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topProducts.map((product, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                    <TableCell>
                      <Chip
                        label={`#${index + 1}`}
                        size="small"
                        sx={{
                          fontWeight: 'bold',
                          bgcolor: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : '#e0e0e0',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">{product.product}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={product.category} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">
                        {product.sales.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">units</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="700" color="primary">
                        {formatCurrency(product.revenue)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Tab 3: Inter-Store Transfers */}
      {activeTab === 3 && (
        <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6" fontWeight="bold">Recent Inter-Store Transfers</Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Transfer No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>From</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>To</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Items</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentTransfers.map((transfer) => (
                  <TableRow key={transfer.id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="700" sx={{ fontFamily: 'monospace' }}>
                        {transfer.transferNo}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">{transfer.from}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="600">{transfer.to}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{transfer.items} items</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(transfer.date).toLocaleDateString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={transfer.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(transfer.status).bg,
                          color: getStatusColor(transfer.status).color,
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default HQDashboard;

